// Libraries
import { useCallback, useEffect, useState } from "react";

// Utils
import { debounceFn } from "@/Common/helpers/functions";

// Components
import GlobalSearch from "./components/GlobalSearch";
import DrawDepartmentSection from "./components/DrawDepartmentSection";

import LoadingSkeleton from "./components/LoadingSkeleton";

// Constants
import { DEBOUNCE_DELAY_500 } from "@/Constants/debounce";

// Types
import { TDrawDepartment } from "./types";
import { IDrawInfo, IOverdrawDetails } from "@/DataPages/types/draw/models";

// Helpers
import { matchDepartments } from "./helpers";

type TDrawsContentProps = {
	draws: IDrawInfo[];
	overdraws: IOverdrawDetails[];
	positions: string[];
	loading: boolean;
};

export default function DrawsContent(props: Readonly<TDrawsContentProps>) {
	const { draws, loading, overdraws, positions } = props;

	const [data, setData] = useState<TDrawDepartment[]>([]);
	const [searchValue, setSearchValue] = useState<string>("");
	const [globalSearch, setGlobalSearch] = useState<string>("");

	const isDataPresent = Boolean(draws?.length || overdraws?.length);

	const handleSearch = (value: string) => {
		setSearchValue(value);
		globalSearchCallBack(value);
	};

	function onClearSearch() {
		setSearchValue("");
		setGlobalSearch("");
	}

	const globalSearchCallBack = useCallback(
		debounceFn((value: string) => setGlobalSearch(value), DEBOUNCE_DELAY_500),
		[]
	);

	function generateKey(value: string) {
		return value.replace(" ", "");
	}

	useEffect(() => {
		if (isDataPresent) {
			const dataToTable = matchDepartments(positions, draws, overdraws);
			setData(dataToTable);
		}

		return () => {
			setData([]);
			setSearchValue("");
		};
	}, [positions, overdraws, draws]);

	return (
		<>
			<GlobalSearch
				loading={Boolean(loading)}
				isDataPresent={isDataPresent}
				value={searchValue}
				onChangeValue={handleSearch}
				onClearSearch={onClearSearch}
			/>

			{loading && <LoadingSkeleton />}

			{!loading && isDataPresent && <DrawDepartmentSection data={data} globalSearch={globalSearch} generateKey={generateKey} />}
		</>
	);
}
